import React from "react";
import {
  createTableColumn,
  DataGrid,
  DataGridBody,
  DataGridCell,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridRow,
  Dropdown,
  Option,
  SearchBox,
  TableCellLayout,
  tokens,
  type TableColumnDefinition,
  type DropdownProps,
  type TableColumnId,
} from "@fluentui/react-components";
import { useMsal } from "@azure/msal-react";
import type { IHistoryItem } from "../../models/IHistoryItem";
import { useLocation } from "react-router-dom";
import { DataService } from "../../services/dataService";
import type { IDokumentItem } from "../../models/IDokumentItem";
import { formatDate } from "../../utils/format";
import Dokumente from "./dokumente";
import { useStyles } from "../../utils/styles";

const History: React.FC = () => {
  const styles = useStyles();
  const location = useLocation();
  const { instance, accounts } = useMsal();
  const accountInfo = accounts[0] || null;
  const dataService = React.useMemo(() => {
    return accountInfo ? new DataService(instance, accountInfo) : null;
  }, [instance, accountInfo]);
  const [filterText, setFilterText] = React.useState<string>("");
  const [aktionFilter, setAktionFilter] =
    React.useState<string>("Alle Aktionen");
  const [typFilter, setTypFilter] = React.useState<string>(
    "Alle Registerkarten",
  );
  const [historyItems, setHistoryItems] = React.useState<IHistoryItem[]>([]);
  const [dokumente, setDokumente] = React.useState<IDokumentItem[]>([]);

  React.useEffect(() => {
    if (!dataService) return;
    if (!location.state?.projektId) return;
    const fetchItems = async () => {
      const response = await dataService.getProjektHistory(
        location.state.projektId,
        filterText,
        typFilter,
        aktionFilter,
      );
      if (response) setHistoryItems(response);
    };
    const fetchDokumente = async () => {
      const response = await dataService.getProjektDokumente(
        location.state.projektId,
      );
      if (response) setDokumente(response);
    };
    fetchItems();
    fetchDokumente();
  }, [
    dataService,
    location.state?.projektId,
    filterText,
    typFilter,
    aktionFilter,
  ]);

  const getColumnStyle = (columnId: TableColumnId): React.CSSProperties =>
    columnId === "zeitpunkt" ? { flex: "0 0 120px", minWidth: "120px" } : {};

  const columnsHistory: TableColumnDefinition<IHistoryItem>[] = [
    createTableColumn<IHistoryItem>({
      columnId: "zeitpunkt",
      renderHeaderCell: () => "Datum/Zeit",
      compare: (a, b) => {
        const aTime = a.zeitpunkt ? new Date(a.zeitpunkt).getTime() : 0;
        const bTime = b.zeitpunkt ? new Date(b.zeitpunkt).getTime() : 0;
        return aTime - bTime;
      },
      renderCell: (item) => (
        <TableCellLayout>
          {item.zeitpunkt ? formatDate(item.zeitpunkt) : ""}
        </TableCellLayout>
      ),
    }),
    createTableColumn<IHistoryItem>({
      columnId: "registerkarte",
      renderHeaderCell: () => "Registerkarte",
      compare: (a, b) =>
        (a.registerkarte ?? "").localeCompare(b.registerkarte ?? ""),
      renderCell: (item) => (
        <TableCellLayout>{item.registerkarte ?? ""}</TableCellLayout>
      ),
    }),
    createTableColumn<IHistoryItem>({
      columnId: "aktion",
      renderHeaderCell: () => "Aktion",
      compare: (a, b) => (a.aktion ?? "").localeCompare(b.aktion ?? ""),
      renderCell: (item) => (
        <TableCellLayout>{item.aktion ?? ""}</TableCellLayout>
      ),
    }),
    createTableColumn<IHistoryItem>({
      columnId: "bemerkung",
      renderHeaderCell: () => "Kommentar",
      compare: (a, b) => (a.bemerkung ?? "").localeCompare(b.bemerkung ?? ""),
      renderCell: (item) => (
        <TableCellLayout>{item.bemerkung ?? ""}</TableCellLayout>
      ),
    }),
    createTableColumn<IHistoryItem>({
      columnId: "name",
      renderHeaderCell: () => "Benutzer*in",
      compare: (a, b) => (a.name ?? "").localeCompare(b.name ?? ""),
      renderCell: (item) => (
        <TableCellLayout>{item.name ?? ""}</TableCellLayout>
      ),
    }),
  ];

  const onTypSelect = React.useCallback<
    NonNullable<DropdownProps["onOptionSelect"]>
  >((_, data) => {
    setTypFilter(
      data.selectedOptions.length > 0
        ? data.selectedOptions[0]
        : "Alle Registerkarten",
    );
  }, []);

  const onAktionSelect = React.useCallback<
    NonNullable<DropdownProps["onOptionSelect"]>
  >((_, data) => {
    setAktionFilter(
      data.selectedOptions.length > 0
        ? data.selectedOptions[0]
        : "Alle Aktionen",
    );
  }, []);

  if (location.state?.projektId === undefined) {
    return (
      <p
        style={{
          fontSize: "16px",
          marginBottom: "30px",
          maxWidth: "500px",
        }}
      >
        Kein Projekt ausgewählt.
      </p>
    );
  }

  const renderReadMode = () => (
    <>
      <div className="verticalLayout">
        <section className={styles.section}>
          <div className={styles.headerRow}>
            <h3 className={styles.sectionTitle}>History</h3>
          </div>
          <div>
            <SearchBox
              placeholder="Suchen..."
              onChange={(_, newValue) => setFilterText(newValue?.value || "")}
              value={filterText}
              className={styles.searchBoxHistory}
            />
            <Dropdown
              title="Filtern nach Registerkarten"
              placeholder="Filtern nach Registerkarten"
              value={typFilter}
              onOptionSelect={onTypSelect}
              style={{ width: "300px" }}
            >
              <Option>Alle Registerkarten</Option>
              <Option>Grunddaten & relevante Personen</Option>
              <Option>Qualifikationsprofil</Option>
              <Option>Projektplan</Option>
              <Option>Kachelmatrix</Option>
              <Option>Curriculummatrix</Option>
              <Option>Konkordanzmatrix</Option>
              <Option>Kompetenzraster</Option>
            </Dropdown>
            <Dropdown
              title="Filtern nach Aktion"
              placeholder="Filtern nach Aktion"
              value={aktionFilter}
              onOptionSelect={onAktionSelect}
              style={{ width: "250px" }}
            >
              <Option>Alle Aktionen</Option>
              <Option>Speichern</Option>
              <Option>Freigabe</Option>
              <Option>Ablehnung</Option>
              <Option>Löschen</Option>
            </Dropdown>
          </div>
          <div className={styles.grid}>
            <DataGrid
              items={historyItems}
              columns={columnsHistory}
              sortable
              size="medium"
              aria-label="History"
              focusMode="composite"
              style={{ width: "100%" }}
            >
              <DataGridHeader>
                <DataGridRow>
                  {({ renderHeaderCell, columnId }) => (
                    <DataGridHeaderCell
                      className="dataGridHeaderCell"
                      style={{
                        ...getColumnStyle(columnId),
                        backgroundColor: tokens.colorNeutralBackground3,
                        color: tokens.colorNeutralForeground1,
                      }}
                    >
                      {renderHeaderCell()}
                    </DataGridHeaderCell>
                  )}
                </DataGridRow>
              </DataGridHeader>
              <DataGridBody<IHistoryItem>>
                {({ item, rowId }) => {
                  const isAlternateRow = Number(rowId) % 2 === 1;

                  return (
                    <DataGridRow
                      key={rowId}
                      style={{
                        backgroundColor: isAlternateRow
                          ? tokens.colorNeutralBackground2
                          : "transparent",
                        color: tokens.colorNeutralForeground1,
                      }}
                    >
                      {(column) => (
                        <DataGridCell
                          className="dataGridCell"
                          style={{
                            ...getColumnStyle(column.columnId),
                            color: tokens.colorNeutralForeground1,
                          }}
                        >
                          {column.renderCell(item)}
                        </DataGridCell>
                      )}
                    </DataGridRow>
                  );
                }}
              </DataGridBody>
            </DataGrid>
          </div>
        </section>
        <Dokumente projektId={location.state.projektId} dokumente={dokumente} />
      </div>
    </>
  );

  return renderReadMode();
};

export default History;
